import React from "react";
import { ASSETS, BUSINESS } from "../lib/assets";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { MenuBoard, MenuJumpBar, useMenu } from "../components/MenuBoard";
import { HolidayCtaButton } from "../components/Holiday";

export default function MenuPage() {
  usePageMeta({
    title: "Menu & Order Online",
    description: "See the full When Pigs Fly Family BBQ menu — brisket, ribs, pulled pork, chicken, wings and homestyle sides. Order online for pickup in Spring Hill, FL.",
    path: "/menu",
  });

  const cats = useMenu();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink bg-ink text-bone" data-testid="menu-hero">
        <div className="absolute inset-0 opacity-40">
          <img src={ASSETS.smokerCloseup} alt="" className="mono-img h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/50" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-4 py-24 md:px-8 md:py-32">
          <div className="eyebrow mb-3 text-bone/60">The pit · Spring Hill, FL 34610</div>
          <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">The Menu</h1>
          <p className="mt-6 max-w-xl font-serif text-base leading-relaxed text-bone/80">
            Smoked daily on-site. Dine-in, takeout, or catering. Every platter comes with your choice of 2 sides.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={BUSINESS.toastOrderUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="menu-toast-btn"
              className="inline-flex items-center gap-2 border-2 border-bone bg-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-colors duration-200 hover:bg-transparent hover:text-bone"
            >
              Order Online <ArrowUpRight size={16} />
            </a>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="menu-call-btn"
              className="inline-flex items-center gap-2 border-2 border-bone bg-transparent px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink"
            >
              Call {BUSINESS.phone}
            </a>
            <HolidayCtaButton testid="menu-holiday-btn" />
          </div>
        </div>
      </section>

      {cats && cats.length > 0 && <MenuJumpBar cats={cats} />}

      <section className="relative border-b border-ink bg-bone py-12 md:py-16" data-testid="menu-list-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          {cats === null ? (
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60"><Loader2 size={14} className="animate-spin" /> Loading menu…</div>
          ) : (
            <MenuBoard cats={cats} />
          )}
          <p className="mt-6 max-w-3xl font-serif text-sm leading-relaxed text-ink/70">
            Every platter is smoked low &amp; slow, sliced or pulled to order. Prices subject to change.
          </p>
        </div>
      </section>

      {/* CATERING CTA */}
      <section className="border-b border-ink bg-bone py-16" data-testid="menu-catering-cta">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-4 md:flex-row md:items-center md:px-8">
          <div>
            <div className="eyebrow mb-2 text-ink/60">Feeding a crowd?</div>
            <h2 className="font-rye text-3xl uppercase leading-none md:text-5xl">
              Ask us about our Catering Options
            </h2>
          </div>
          <Link
            to="/catering"
            data-testid="menu-catering-btn"
            className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink"
          >
            Request a Catering Quote <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-ink bg-ink py-16 text-bone" data-testid="menu-final-cta">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-4 md:flex-row md:items-center md:px-8">
          <div>
            <div className="eyebrow mb-2 text-bone/60">Ready to eat?</div>
            <h2 className="font-alfa text-3xl uppercase leading-none md:text-4xl">Place your order now</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={BUSINESS.toastOrderUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="menu-cta-toast-btn"
              className="inline-flex items-center gap-2 border-2 border-bone bg-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-colors duration-200 hover:bg-transparent hover:text-bone"
            >
              Order Online <ArrowUpRight size={16} />
            </a>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="menu-cta-call-btn"
              className="inline-flex items-center gap-2 border-2 border-bone bg-transparent px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink"
            >
              Call to Order
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
